import { __assign, __rest } from "tslib";
import React, { useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import clsx from 'clsx';
import { getBaseProps } from '../internal/base-component';
import { fireNonCancelableEvent } from '../internal/events';
import { KeyCode } from '../internal/keycode';
import { useUniqueId } from '../internal/hooks/use-unique-id';
import { InternalButton } from '../button/internal';
import InternalHeader from '../header/internal';
import Portal from '../internal/components/portal';
import { useContainerBreakpoints } from '../internal/hooks/container-queries';
import styles from './styles.css.js';
import { disableBodyScrolling, enableBodyScrolling } from './body-scroll';
import mergeRefs from 'react-merge-refs';
export default function InternalModal(_a) {
    var _b;
    var _c = _a.size, size = _c === void 0 ? 'medium' : _c, visible = _a.visible, _d = _a.closeAriaLabel, closeAriaLabel = _d === void 0 ? '' : _d, header = _a.header, children = _a.children, footer = _a.footer, onDismiss = _a.onDismiss, modalRoot = _a.modalRoot, _e = _a.__internalRootRef, __internalRootRef = _e === void 0 ? null : _e, rest = __rest(_a, ["size", "visible", "closeAriaLabel", "header", "children", "footer", "onDismiss", "modalRoot", "__internalRootRef"]);
    var instanceUniqueId = useUniqueId();
    var headerId = (rest.id || instanceUniqueId) + "-header";
    var dialogRef = useRef(null);
    var focusLockRef = useRef(null);
    var initiallyFocusedElementRef = useRef(undefined);
    var _f = useContainerBreakpoints(['xs']), breakpoint = _f[0], rootRef = _f[1];
    var baseProps = getBaseProps(rest);
    // enable body scroll and restore focus if unmounting while visible
    useEffect(function () {
        return function () {
            enableBodyScrolling();
        };
    }, []);
    // enable / disable body scroll
    useEffect(function () {
        if (visible) {
            disableBodyScrolling();
        }
        else {
            enableBodyScrolling();
        }
    }, [visible]);
    // Because we hide the element with styles (and not actually detach it from DOM), we need to scroll to top
    useEffect(function () {
        if (visible && rootRef.current) {
            rootRef.current.scrollTop = 0;
        }
    }, [rootRef, visible]);
    // Handle focus restore
    var onActivation = function () {
        initiallyFocusedElementRef.current = document.activeElement;
    };
    var onDeactivation = function () {
        var _a;
        (_a = initiallyFocusedElementRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        initiallyFocusedElementRef.current = undefined;
    };
    var dismiss = function (reason) { return fireNonCancelableEvent(onDismiss, { reason: reason }); };
    var onOverlayClick = function (event) {
        if (event.target === rootRef.current) {
            dismiss('overlay');
        }
    };
    var onCloseButtonClick = function () { return dismiss('closeButton'); };
    var escKeyHandler = function (event) {
        if (event.keyCode === KeyCode.escape) {
            dismiss('keyboard');
        }
    };
    return (React.createElement(Portal, { container: modalRoot },
        React.createElement("div", __assign({}, baseProps, { className: clsx(styles.root, (_b = {}, _b[styles.hidden] = !visible, _b), baseProps.className), role: "dialog", "aria-modal": true, "aria-labelledby": headerId, onClick: onOverlayClick, ref: mergeRefs([rootRef, __internalRootRef]) }),
            React.createElement(FocusLock, { disabled: !visible, autoFocus: true, className: styles['focus-lock'], ref: focusLockRef, onActivation: onActivation, onDeactivation: onDeactivation },
                React.createElement("div", { className: clsx(styles.dialog, styles[size], styles["breakpoint-" + breakpoint]), onKeyDown: escKeyHandler, tabIndex: -1, ref: dialogRef },
                    React.createElement("div", { className: styles.container },
                        React.createElement("div", { id: headerId, className: styles.header },
                            React.createElement(InternalHeader, { variant: "h2", className: styles['inner-header'], actions: React.createElement(InternalButton, { ariaLabel: closeAriaLabel, className: styles['dismiss-control'], variant: "icon", iconName: "close", formAction: "none", onClick: onCloseButtonClick }) },
                                React.createElement("span", { className: styles['header--text'] }, header))),
                        React.createElement("div", { className: styles.content }, children),
                        footer && React.createElement("div", { className: styles.footer }, footer)))))));
}
//# sourceMappingURL=internal.js.map